import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import TenantContext from '../../contexts/tenantContext';
import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  Pager,
  Paging,
  FilterRow,
  Editing,
  Form,
  Button as GridButton,
  Toolbar,
  Item as GridItem,
  DataGridRef,
  Popup,
  Lookup,
  Label,
  DataGridTypes
} from 'devextreme-react/data-grid';
import { EmergenzaDTO, EmergenzaNonAllocatiDTO, EmergenzeApi } from '../../data';
import { confirm } from 'devextreme/ui/dialog';
import { apiConfiguration } from '../../apiConfig';
import { notifyError } from '../../utils/errorHandling';
import { useEmergenzaAssistitiDataSource } from '../../datasources/EmergenzaAssistitiDataSource';
import EmergenzaAssegnazionePopup, { EmergenzaAssegnazionePopupHandle } from './EmergenzaAssegnazionePopup';
import {
  LinearGauge, Scale, MinorTick, Export, Title, Font,
  ValueIndicator,
} from 'devextreme-react/linear-gauge';
import { Anchor, Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from 'file-saver';

type Props = {
  emergenza: EmergenzaDTO;
  onChanged?: () => void;
}

export type EmergenzaEditPanelHandle = {
  save: () => Promise<boolean>;
}

const EmergenzaEditPanel: React.ForwardRefRenderFunction<EmergenzaEditPanelHandle, Props> = (props, ref) => {
  const { currentTenant } = useContext(TenantContext);
  const dataGridRef = useRef<DataGridRef>(null);
  const emergenzaAssegnazionePopupRef = useRef<EmergenzaAssegnazionePopupHandle>(null);
  const [emergenzeNonAllocate, setEmergenzeNonAllocate] = useState<EmergenzaNonAllocatiDTO>();

  const emergenzeAssistitiDataSource = useEmergenzaAssistitiDataSource(String(props.emergenza.id));

  const creaEmergenzaHandle = () => {
    emergenzaAssegnazionePopupRef.current?.new();
  };

  const chiudiEmergenzaHandle = async () => {
    var result = await confirm("Chiudere l'emergenza corrente?", props.emergenza.nome ?? "Emergenza");
    if (result) {
      try {
        var api = new EmergenzeApi(apiConfiguration);
        await api.closeCurrentEmergenza({ tenantId: currentTenant.Id });

        props.onChanged?.();
      }
      catch (e) {
        notifyError(e);
      }
    }
  };

  const refreshGrid = () => dataGridRef.current?.instance().refresh();

  const refreshGauge = async () => {
    var api = new EmergenzeApi(apiConfiguration);
    var na = await api.getAssistitiNonAllocati({ tenantId: currentTenant.Id });

    setEmergenzeNonAllocate(na);

    console.log(na);
  };

  const assistitoStatusCell = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    const gridCellData = cellData.value;

    if (gridCellData === true)
      return <span style={{ color: 'green' }} title='Assistenza completata'><i className="dx-icon-check"></i></span>;
    else if (gridCellData === false)
      return <span style={{ color: 'red' }} title='Assitito non trovato'><i className="dx-icon-close"></i></span>;
    else
      return <span title='Assistenza non ancora effettuata'><i className="dx-icon-help"></i></span>;
  };

  useEffect(() => { refreshGauge() }, [props.emergenza]);

  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === 'data') {
          if (gridCell?.column?.dataField === 'assistenza') {
            console.log("customizeCell", gridCell);
            if (gridCell.value === undefined) {
              excelCell.value = 'Assistenza non ancora effettuata';
            }
            else {
              excelCell.value = gridCell.value ? 'Assistenza completata' : 'Assitito non trovato';
              excelCell.font = { color: gridCell.value ? { argb: 'FF00FF00' } : { argb: 'FFFF0000' } };
            }
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'ElencoAssistiti.xlsx');
      });
    });
  };

  return (
    <React.Fragment>
      <LinearGauge
        value={emergenzeNonAllocate?.assistitiAllocati}
        animation={{ enabled: false }}
      >
        <Scale startValue={0} endValue={emergenzeNonAllocate?.assistitiTotali ?? 0} tickInterval={1}  >
          {/* <MinorTick visible={true} /> */}
        </Scale>
        <Export enabled={false} />
        <ValueIndicator
          type="rangeBar"
          color={emergenzeNonAllocate?.assistitiNonAllocati === 0 ? "blue" : "red"}
        />
        <Title text="Stato assegnazione">
          <Font size={14} />
        </Title>
      </LinearGauge>

      <DataGrid
        ref={dataGridRef}
        dataSource={emergenzeAssistitiDataSource}
        showBorders={true}
        columnAutoWidth={true}
        columnHidingEnabled={true}
        remoteOperations={false}
        noDataText={"Nessun associato."}
        onRowRemoved={refreshGauge}
        onExporting={onExporting}
      >
        <Paging enabled={true} />
        <Export enabled={true} />
        <Editing
          mode="row"
          allowDeleting={true}
        />

        <Column type='buttons' alignment='left' />
        <Column dataField="ente.nome" caption="Nome Ente" groupIndex={0} />
        <Column dataField="assistito.nome" caption="Nome" />
        <Column dataField="assistito.cognome" caption="Cognome" />
        <Column dataField="assistenza" caption="Assistenza" cellRender={assistitoStatusCell} />
        <Toolbar>
          <GridItem location="before" text="Associazioni enti/assistiti">
          </GridItem>
          <GridItem
            location="after"
            locateInMenu="auto"
            widget="dxButton"
            options={{
              hint: "Crea una nuova associazione",
              text: "Nuova Associazione",
              stylingMode: "text",
              icon: "add",
              onClick: creaEmergenzaHandle,
            }}
          />

          <GridItem
            location="after"
            locateInMenu="auto"
            widget="dxButton"
            options={{
              hint: "L'emergenza viene chiusa e storicizzata",
              text: "Chiudi emergenza",
              stylingMode: "text",
              icon: "square",
              onClick: chiudiEmergenzaHandle,
            }}
          />

          <GridItem location="after" name='exportButton' />
          {/* <GridItem
            location="after"
            locateInMenu="auto"
            widget="dxButton"
            options={{
              hint: "L'emergenza viene chiusa e storicizzata",
              text: "Chiudi emergenza",
              stylingMode: "text",
              icon: "square",
              onClick: chiudiEmergenzaHandle,
            }}
          /> */}

          {/* <GridItem location="after" locateInMenu='auto'>
            <Button
              hint="Crea una nuova associazione"
              text="Nuova Associazione"
              stylingMode="text"
              icon="add"
              onClick={creaEmergenzaHandle}
            />
            <Button
              hint="L'emergenza viene chiusa e storicizzata"
              text="Chiudi emergenza"
              stylingMode="text"
              icon="square"
              onClick={chiudiEmergenzaHandle}
            />
          </GridItem> */}
        </Toolbar>
      </DataGrid>

      <EmergenzaAssegnazionePopup ref={emergenzaAssegnazionePopupRef} emergenzaId={String(props.emergenza.id)} onDataChanged={() => { refreshGrid(); refreshGauge() }} />
    </React.Fragment>
  );
}

export default React.forwardRef(EmergenzaEditPanel);
